import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  BanknotesIcon,
  ChartBarIcon,
  PlayIcon,
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import { paperTradingApi, strategiesApi } from '@/services/api';
import { formatCurrency, formatPercentage, formatDate, formatCryptoPrice, formatCryptoQuantity, formatCryptoAmount } from '@/utils/formatters';
import LoadingSpinner from '@/components/UI/LoadingSpinner';
import SummaryCard from '@/components/UI/SummaryCard';
import ConfirmDialog from '@/components/UI/ConfirmDialog';

interface PaperTrade {
  id: string;
  strategyId: string;
  strategyName: string;
  symbol: string;
  tradeType: 'buy' | 'sell';
  quantity: number;
  price: number;
  totalCost: number;
  fee: number;
  status: 'pending' | 'filled' | 'cancelled';
  executedAt: string;
  notes?: string;
  pnl?: number;
  pnlPercentage?: number;
}

interface PaperPortfolio {
  totalValue: number;
  cashBalance: number;
  totalPnL: number;
  totalPnLPercentage: number;
  initialBalance: number;
  positions: Array<{
    symbol: string;
    quantity: number;
    averagePrice: number;
    currentValue: number;
    unrealizedPnL: number;
    unrealizedPnLPercentage: number;
  }>;
}

interface Strategy {
  id: string;
  name: string;
  type: string;
  isActive: boolean;
  isPaperTrading: boolean;
  performance?: {
    totalTrades: number;
    winRate: number;
    totalReturn: number;
  };
}

const PaperTradingPage: React.FC = () => {
  const [portfolio, setPortfolio] = useState<PaperPortfolio | null>(null);
  const [trades, setTrades] = useState<PaperTrade[]>([]);
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStrategy, setSelectedStrategy] = useState<string>('all');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState<PaperTrade | null>(null);
  const [showTradeDetails, setShowTradeDetails] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [portfolioRes, strategiesRes] = await Promise.allSettled([
        paperTradingApi.getPortfolio(),
        strategiesApi.getAll({ limit: 50 })
      ]);

      if (portfolioRes.status === 'fulfilled') {
        const rawPortfolio = (portfolioRes.value as any).data?.portfolio || {};
        
        // Map backend field names to frontend expectations
        const mappedPortfolio = {
          totalValue: rawPortfolio.totalValue || 0,
          cashBalance: rawPortfolio.cashAvailable || 0, // Backend: cashAvailable -> Frontend: cashBalance
          totalPnL: rawPortfolio.totalReturn || 0, // Backend: totalReturn -> Frontend: totalPnL
          totalPnLPercentage: rawPortfolio.totalReturnPercentage || 0, // Backend: totalReturnPercentage -> Frontend: totalPnLPercentage  
          initialBalance: rawPortfolio.performance?.initialBalance || rawPortfolio.totalInvested || 10000, // Use performance.initialBalance or fallback
          positions: rawPortfolio.positions || []
        };
        
        setPortfolio(mappedPortfolio);
        
        // Extract trades from portfolio balance history
        let extractedTrades = [];
        if (rawPortfolio.performance?.balanceHistory) {
          extractedTrades = rawPortfolio.performance.balanceHistory
            .filter((entry: any) => entry.trade)
            .map((entry: any) => {
              const trade = entry.trade;
              // Map snake_case backend fields to camelCase frontend fields
              return {
                id: trade.id,
                strategyId: trade.strategy_id,
                strategyName: trade.strategy_name,
                symbol: trade.symbol,
                tradeType: trade.trade_type, // snake_case -> camelCase
                quantity: parseFloat(trade.quantity || 0),
                price: parseFloat(trade.price || 0),
                totalCost: parseFloat(trade.total_cost || 0), // snake_case -> camelCase
                fee: parseFloat(trade.fee || 0),
                status: trade.status,
                executedAt: trade.executed_at, // snake_case -> camelCase
                notes: trade.notes, // Add notes
                pnl: trade.pnl,
                pnlPercentage: trade.pnl_percentage
              };
            });
        }
        
        // Sort trades by date descending (latest first)
        const sortedTrades = (extractedTrades || []).sort((a: PaperTrade, b: PaperTrade) => {
          const dateA = new Date(a.executedAt || 0);
          const dateB = new Date(b.executedAt || 0);
          return dateB.getTime() - dateA.getTime(); // Descending order
        });
        
        setTrades(sortedTrades);
      } else {
        console.warn('Portfolio fetch failed:', portfolioRes.reason);
      }

      if (strategiesRes.status === 'fulfilled') {
        setStrategies((strategiesRes.value as any).data?.strategies || []);
      } else {
        console.warn('Strategies fetch failed:', strategiesRes.reason);
      }

      // If all failed, show error
      if (portfolioRes.status === 'rejected' && 
          strategiesRes.status === 'rejected') {
        throw new Error('Failed to load paper trading data');
      }

    } catch (error: any) {
      console.error('Paper trading fetch error:', error);
      setError(error.message || 'Failed to load paper trading data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExecuteStrategy = async (strategyId?: string) => {
    try {
      setExecuting(true);
      
      await paperTradingApi.executeStrategy(strategyId);
      
      toast.success(strategyId ? 'Strategy executed successfully' : 'All strategies executed successfully');
      
      // Refresh data
      await fetchData();
      
    } catch (error: any) {
      console.error('Execute strategy error:', error);
      toast.error(error.response?.data?.message || 'Failed to execute strategy');
    } finally {
      setExecuting(false);
    }
  };

  const handleResetPortfolio = async () => {
    try {
      await paperTradingApi.resetPortfolio();
      toast.success('Portfolio reset successfully');
      setShowResetDialog(false);
      await fetchData();
    } catch (error: any) {
      console.error('Reset portfolio error:', error);
      toast.error(error.response?.data?.message || 'Failed to reset portfolio');
    }
  };

  const handleViewTrade = (trade: PaperTrade) => {
    setSelectedTrade(trade);
    setShowTradeDetails(true);
  };

  const handleDeleteTrade = async (tradeId: string) => {
    try {
      // Use specific trade deletion endpoint
      await paperTradingApi.deleteTrade(tradeId);
      toast.success('Trade deleted successfully');
      await fetchData();
    } catch (error: any) {
      console.error('Delete trade error:', error);
      toast.error(error.response?.data?.message || 'Failed to delete trade');
    }
  };

  const getFilteredTrades = () => {
    if (selectedStrategy === 'all') return trades;
    return trades.filter(trade => trade.strategyId === selectedStrategy);
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'filled': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'unknown':
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && !portfolio) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !portfolio) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to load paper trading data</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Paper Trading</h1>
          <p className="text-sm text-gray-600">Practice trading with virtual money - test your strategies risk-free</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={() => handleExecuteStrategy()}
            disabled={executing || strategies.length === 0}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {executing ? (
              <LoadingSpinner size="sm" className="mr-2" />
            ) : (
              <PlayIcon className="h-4 w-4 mr-2" />
            )}
            Execute All Strategies
          </button>
          
          <button
            onClick={() => setShowResetDialog(true)}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Reset Portfolio
          </button>
        </div>
      </div>

      {/* Portfolio Summary */}
      {portfolio && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <SummaryCard
            title="Portfolio Value"
            value={formatCurrency(portfolio.totalValue)}
            icon={BanknotesIcon}
            trend={portfolio.totalPnL >= 0 ? 'up' : 'down'}
            change={portfolio.totalPnLPercentage}
            changeType="percentage"
            loading={loading}
          />
          
          <SummaryCard
            title="Cash Balance"
            value={formatCurrency(portfolio.cashBalance)}
            icon={BanknotesIcon}
            loading={loading}
          />
          
          <SummaryCard
            title="Total P&L"
            value={formatCurrency(portfolio.totalPnL)}
            icon={ChartBarIcon}
            trend={portfolio.totalPnL >= 0 ? 'up' : 'down'}
            change={portfolio.totalPnLPercentage}
            changeType="percentage"
            loading={loading}
          />
          
          <SummaryCard
            title="Initial Balance"
            value={formatCurrency(portfolio.initialBalance)}
            icon={BanknotesIcon}
            loading={loading}
          />
        </div>
      )}

      {/* Active Strategies */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Active Paper Trading Strategies</h3>
            <button className="flex items-center text-sm text-indigo-600 hover:text-indigo-800">
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Strategy
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {strategies.length === 0 ? (
            <div className="text-center py-8">
              <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No active strategies</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Create a paper trading strategy to start testing your approach
              </p>
              <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md">
                Create Strategy
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {strategies.map((strategy) => (
                <div key={strategy.id} className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900 dark:text-white">{strategy.name}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      strategy.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {strategy.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">{strategy.type}</p>
                  
                  {strategy.performance && (
                    <div className="text-xs text-gray-500 mb-3">
                      <div>Trades: {strategy.performance.totalTrades}</div>
                      <div>Win Rate: {formatPercentage(strategy.performance.winRate)}</div>
                      <div>Return: {formatPercentage(strategy.performance.totalReturn)}</div>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <button
                      onClick={() => handleExecuteStrategy(strategy.id)}
                      disabled={executing || !strategy.isActive}
                      className="text-sm text-indigo-600 hover:text-indigo-800 disabled:text-gray-400"
                    >
                      Execute
                    </button>
                    <button className="text-sm text-gray-600 hover:text-gray-800">
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Current Positions */}
      {portfolio && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Current Positions</h3>
          </div>
          
          {portfolio.positions && portfolio.positions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Symbol
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avg Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current Value
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unrealized P&L
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {portfolio.positions.map((position, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {position.symbol || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCryptoQuantity(position.quantity || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCryptoPrice(position.averagePrice || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCryptoAmount(position.currentValue || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className={`text-sm font-medium ${
                            (position.unrealizedPnL || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatCryptoAmount(position.unrealizedPnL || 0)}
                          </span>
                          <span className={`text-xs ml-1 ${
                            (position.unrealizedPnL || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            ({formatPercentage(position.unrealizedPnLPercentage || 0)})
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 px-6">
              <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Open Positions</h3>
              <p className="text-gray-600 dark:text-gray-300">
                All positions are currently closed. Execute a BUY strategy to open a new position.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Trade History */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Trade History</h3>
            
            <div className="flex items-center space-x-3">
              <select
                value={selectedStrategy}
                onChange={(e) => setSelectedStrategy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">All Strategies</option>
                {strategies.map((strategy) => (
                  <option key={strategy.id} value={strategy.id}>
                    {strategy.name}
                  </option>
                ))}
              </select>
              
              <button className="flex items-center text-sm text-indigo-600 hover:text-indigo-800">
                <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                Export
              </button>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Symbol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getFilteredTrades().map((trade) => (
                <tr key={trade.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {trade.symbol || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      trade.tradeType === 'buy' ? 'bg-green-100 text-green-800' : 
                      trade.tradeType === 'sell' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {trade.tradeType?.toUpperCase() || 'UNKNOWN'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCryptoQuantity(trade.quantity || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCryptoPrice(trade.price || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCryptoAmount(trade.totalCost || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(trade.status || 'unknown')}`}>
                      {trade.status || 'unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {trade.executedAt ? formatDate(trade.executedAt, 'MMM dd, yyyy HH:mm') : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => handleViewTrade(trade)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="View trade details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTrade(trade.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete this trade"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {getFilteredTrades().length === 0 && (
            <div className="text-center py-8">
              <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No trades yet</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Execute a strategy to start your paper trading journey
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Trade Details Modal */}
      {showTradeDetails && selectedTrade && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
            <div className="mt-3">
              {/* Modal Header */}
                              <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Trade Details</h3>
                  <button
                    onClick={() => setShowTradeDetails(false)}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
                  >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Trade Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Trading Pair</label>
                    <p className="text-sm text-gray-900 dark:text-gray-100">{selectedTrade.symbol || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Trade Type</label>
                    <p className="text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        selectedTrade.tradeType === 'buy' ? 'bg-green-100 text-green-800' : 
                        selectedTrade.tradeType === 'sell' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedTrade.tradeType?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Quantity</label>
                    <p className="text-sm text-gray-900 dark:text-gray-100">{formatCryptoQuantity(selectedTrade.quantity || 0)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Price</label>
                    <p className="text-sm text-gray-900 dark:text-gray-100">{formatCryptoPrice(selectedTrade.price || 0)}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Cost</label>
                    <p className="text-sm text-gray-900 dark:text-gray-100">{formatCryptoAmount(selectedTrade.totalCost || 0)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Fee</label>
                    <p className="text-sm text-gray-900 dark:text-gray-100">{formatCryptoAmount(selectedTrade.fee || 0)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</label>
                    <p className="text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(selectedTrade.status || 'unknown')}`}>
                        {selectedTrade.status || 'unknown'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Executed Date</label>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {selectedTrade.executedAt ? formatDate(selectedTrade.executedAt, 'MMM dd, yyyy HH:mm:ss') : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Strategy Information */}
              {selectedTrade.strategyName && (
                <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mb-6">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Strategy Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Strategy Name</label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{selectedTrade.strategyName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Trade ID</label>
                      <p className="text-sm text-gray-900 dark:text-gray-100 font-mono">{selectedTrade.id}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* P&L Information (if available) */}
              {(selectedTrade.pnl !== undefined || selectedTrade.pnlPercentage !== undefined) && (
                <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mb-6">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Profit & Loss</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedTrade.pnl !== undefined && (
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Realized P&L</label>
                        <p className={`text-sm font-medium ${selectedTrade.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCryptoAmount(selectedTrade.pnl)}
                        </p>
                      </div>
                    )}
                    {selectedTrade.pnlPercentage !== undefined && (
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Return %</label>
                        <p className={`text-sm font-medium ${selectedTrade.pnlPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatPercentage(selectedTrade.pnlPercentage)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes (if available) */}
              {selectedTrade.notes && (
                <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mb-6">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Notes</h4>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{selectedTrade.notes}</p>
                </div>
              )}

              {/* Action Buttons */}
                              <div className="flex items-center justify-end space-x-3 border-t border-gray-200 dark:border-gray-600 pt-4">
                <button
                  onClick={() => setShowTradeDetails(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleDeleteTrade(selectedTrade.id);
                    setShowTradeDetails(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                >
                  Delete Trade
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Portfolio Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showResetDialog}
        onClose={() => setShowResetDialog(false)}
        onConfirm={handleResetPortfolio}
        title="Reset Portfolio"
        message="Are you sure you want to reset your paper trading portfolio? This will delete all positions and trades, and reset your balance to the initial amount."
        confirmText="Reset Portfolio"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
};

export default PaperTradingPage; 